import Redirect from "@/taskpane/components/redirect/Redirect";
import LoadingScreen from "@/taskpane/components/loading/LoadingScreen";
import { AuthContext } from "@/taskpane/contexts/auth/AuthContext";
import { useGetUserAttributesQuery } from "@/taskpane/services/auth.hook";
import { isValidCognitoToken, decodeCognitoToken, getTokenExpirationTime } from "@/taskpane/utils/jwt";
import React, { useContext, useEffect } from "react";
import { Navigate, useMatch, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

// Helper function to check if URL contains any token parameters
const hasTokenInUrl = (): boolean => {
  const hash = window.location.hash;
  const search = window.location.search;
  const hasToken = !!(hash && (hash.includes('?idToken=') || hash.includes('?token=') || hash.includes('&idToken='))) || 
                   !!(search && (search.includes('idToken=') || search.includes('token=')));
  // console.log('[hasTokenInUrl] hash:', hash, 'search:', search, 'hasToken:', hasToken);

  return hasToken;
};

// Helper function to extract all tokens from URL
const extractTokensFromUrl = (): { idToken: string | null; accessToken: string | null; refreshToken: string | null; expiresIn: string | null } => {
  const hash = window.location.hash;
  const search = window.location.search;
  
  // console.log('[extractTokensFromUrl] hash:', hash);
  // console.log('[extractTokensFromUrl] search:', search);
  
  let params: URLSearchParams | null = null;
  
  if (hash && hash.includes('?')) {
    const hashQueryString = hash.substring(hash.indexOf('?') + 1);
    // console.log('[extractTokensFromUrl] hashQueryString:', hashQueryString);
    params = new URLSearchParams(hashQueryString);
  } else if (search) {
    params = new URLSearchParams(search);
  }
  
  if (params) {
    // Check for new format (multiple tokens)
    const idToken = params.get('idToken');
    const accessToken = params.get('accessToken');
    const refreshToken = params.get('refreshToken');
    const expiresIn = params.get('expiresIn');
    
    // Fallback to old format (single token)
    const legacyToken = params.get('token');
    
    // console.log('[extractTokensFromUrl] Found tokens:', {
    //   idToken: idToken ? 'PRESENT' : 'MISSING',
    //   accessToken: accessToken ? 'PRESENT' : 'MISSING',
    //   refreshToken: refreshToken ? 'PRESENT' : 'MISSING',
    //   expiresIn: expiresIn || 'MISSING',
    //   legacyToken: legacyToken ? 'PRESENT' : 'MISSING'
    // });
    
    return {
      idToken: idToken || legacyToken,
      accessToken: accessToken,
      refreshToken: refreshToken,
      expiresIn: expiresIn
    };
  }
  
  // console.log('[extractTokensFromUrl] No tokens found in URL');
  return { idToken: null, accessToken: null, refreshToken: null, expiresIn: null };
};

/**
 * ProtectedRoute Component
 *
 * Ensures that the wrapped component is only accessible to authenticated users.
 * Redirects to the login page if the user is not authenticated.
 */
export function ProtectedRoute({
  children,
  ignoreCrpcen = false,
}: {
  children: React.ReactNode;
  ignoreCrpcen?: boolean;
}) {
  const { isAuthenticated, isLoading: authLoading, tokens, isProcessingUrlToken, setTokens } = useContext(AuthContext);
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Only enable user attributes query if we have a valid AccessToken
  const hasValidAccessToken = Boolean(tokens?.AccessToken && tokens.AccessToken.length > 0);
  const { data, isPending } = useGetUserAttributesQuery({
    enabled: isAuthenticated && hasValidAccessToken,
  });

  // Debug SSO user data (commented out for production)
  // useEffect(() => {
  //   if (hasValidAccessToken && !isPending && data) {
  //     console.log('[ProtectedRoute] User data loaded:', {
  //       email: data.email,
  //       crpcen: data.crpcen,
  //       hasCrpcen: !!data.crpcen,
  //       crpcenType: typeof data.crpcen,
  //       crpcenLength: data.crpcen?.length
  //     });
  //   }
  // }, [hasValidAccessToken, isPending, data]);

  const urlHasToken = hasTokenInUrl();
  const urlTokens = extractTokensFromUrl();
  const isUrlTokenValid = urlTokens.idToken ? isValidCognitoToken(urlTokens.idToken) : false;

  // console.log('[ProtectedRoute] Route check for:', location.pathname);
  // console.log('[ProtectedRoute] Auth state - isAuthenticated:', isAuthenticated, 'authLoading:', authLoading);
  // console.log('[ProtectedRoute] User data - isPending:', isPending, 'data:', data);
  // console.log('[ProtectedRoute] URL has token:', urlHasToken);
  // console.log('[ProtectedRoute] URL tokens valid:', {
  //   idToken: urlTokens.idToken ? 'VALID' : 'MISSING',
  //   accessToken: urlTokens.accessToken ? 'PRESENT' : 'MISSING',
  //   refreshToken: urlTokens.refreshToken ? 'PRESENT' : 'MISSING'
  // });
  // console.log('[ProtectedRoute] Has valid AccessToken:', hasValidAccessToken);
  // console.log('[ProtectedRoute] Is processing URL token:', isProcessingUrlToken);
  // console.log('[ProtectedRoute] Current tokens:', tokens ? 'TOKENS_PRESENT' : 'NO_TOKENS');

  // Process tokens if they're in the URL but haven't been processed yet
  useEffect(() => {
    if (urlHasToken && isUrlTokenValid && urlTokens.idToken && !isAuthenticated && !authLoading && !isProcessingUrlToken) {
      // console.log('[ProtectedRoute] Valid tokens in URL but not processed, processing now...');
      
      // Calculate expiration time
      let expiresIn = 3600; // Default 1 hour
      if (urlTokens.expiresIn) {
        expiresIn = parseInt(urlTokens.expiresIn, 10);
      } else if (urlTokens.idToken) {
        // Try to get expiration from ID token
        const expirationTime = getTokenExpirationTime(urlTokens.idToken);
        const currentTime = Math.floor(Date.now() / 1000);
        if (expirationTime) {
          expiresIn = expirationTime - currentTime;
        }
      }
      
      const tempTokens = {
        IdToken: urlTokens.idToken || '',
        AccessToken: urlTokens.accessToken || '',
        RefreshToken: urlTokens.refreshToken || '',
        TokenType: 'Bearer',
        ExpiresIn: Math.max(expiresIn, 0)
      };
      
      // console.log('[ProtectedRoute] Setting all tokens from URL:', {
      //   hasIdToken: !!tempTokens.IdToken,
      //   hasAccessToken: !!tempTokens.AccessToken,
      //   hasRefreshToken: !!tempTokens.RefreshToken,
      //   expiresIn: tempTokens.ExpiresIn
      // });

      setTokens(tempTokens);
      
      // Invalidate user data queries to force refetch with new tokens
      setTimeout(() => {
        // console.log('[ProtectedRoute] Invalidating user queries after token set');
        queryClient.invalidateQueries({ queryKey: ['getUserData'] });
        queryClient.invalidateQueries({ queryKey: ['getUserAttributes'] });
        queryClient.invalidateQueries({ queryKey: ['getFolders'] });
      }, 100);
      
      // Clean the URL - remove all token parameters
      if (window.location.hash && window.location.hash.includes('?')) {
        const cleanHash = window.location.hash.split('?')[0];
        window.location.hash = cleanHash;
      }
    }
  }, [urlHasToken, isUrlTokenValid, urlTokens, isAuthenticated, authLoading, isProcessingUrlToken, setTokens, queryClient]);

  // If we're still loading auth OR if we're actively processing a URL token, show loading
  if(authLoading || isProcessingUrlToken) {
    // console.log('[ProtectedRoute] Auth still loading or processing URL token, showing loading screen');
    return <LoadingScreen />;
  }

  // If there's a valid token in the URL, wait for it to be processed
  // This handles the case where isProcessingUrlToken might have been set to false
  // but the authentication state hasn't been updated yet
  if(urlHasToken && isUrlTokenValid && !isAuthenticated) {
    // console.log('[ProtectedRoute] Valid token in URL but not authenticated yet, showing loading screen');
    return <LoadingScreen />;
  }

  // Only redirect to login if there's no token in URL and user is not authenticated
  if(!authLoading && !isAuthenticated && !urlHasToken && !isProcessingUrlToken) {
    // console.log('[ProtectedRoute] Not authenticated and no token in URL, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Check if this is a filiation URL
  const isFiliationUrl = location.pathname.includes("/folder/") && location.pathname.includes("/filiation/");
  // console.log('[ProtectedRoute] Is filiation URL:', isFiliationUrl);

  // Don't redirect filiation URLs immediately
  if (isFiliationUrl) {
    if (hasValidAccessToken && isPending) {
      // console.log('[ProtectedRoute] Filiation URL - user data pending, showing loading screen');
      return <LoadingScreen />;
    }
    // console.log('[ProtectedRoute] Filiation URL - allowing access');
    return <Redirect>{children}</Redirect>;
  }

  // Normal authentication flow for other routes
  if (!isAuthenticated) {
    // console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // If we have a valid AccessToken, check user data and CRPCEN
  if (hasValidAccessToken) {
    // console.log('[ProtectedRoute] Valid AccessToken present, checking user data and CRPCEN');
    if (!isPending && !data) {
      console.error('[ProtectedRoute] No user data available, redirecting to login');
      return <Navigate to="/login" replace />;
    }

    if (!ignoreCrpcen && !isPending && data && (!data?.crpcen || data?.crpcen?.includes("NEED_ACCEPT"))) {
      // console.log('[ProtectedRoute] CRPCEN validation failed, redirecting to CRPCEN page. data.crpcen:', data?.crpcen);
      return <Navigate to="/crpcen" replace />;
    }

    // if (!ignoreCrpcen && !isPending && data && data.crpcen) {
    //   console.log('[ProtectedRoute] CRPCEN is valid:', data.crpcen);
    // }

    if (isPending) {
      // console.log('[ProtectedRoute] User data pending, showing loading screen');
      return <LoadingScreen />;
    }
  }
  // else {
  //   // If we only have IdToken (no AccessToken), allow access without user data validation
  //   console.log('[ProtectedRoute] Authenticated with IdToken only, allowing access without user data validation');
  // }
  // console.log('[ProtectedRoute] All checks passed, allowing access to protected route');
  return <Redirect>{children}</Redirect>;
}

/**
 * ProtectedRoute Component
 *
 * Ensures that the wrapped component is only accessible to authenticated users.
 * Redirects to the login page if the user is not authenticated.
 */
export function NonAuthRouteGuard({ children }: { children: React.ReactNode }) {
  const matchDiagram = useMatch("/filiation/:client_id/:dossier_id/:sous_dossier_id/:flow_id");
  const matchSsoCallback = useMatch("/auth/sso/callback");
  const matchSsoPolling = useMatch("/auth/sso/polling");
  const location = useLocation();

  const { isAuthenticated, isLoading } = useContext(AuthContext);
  
  // console.log('[NonAuthRouteGuard] Guard check:', {
  //   path: location.pathname,
  //   isAuthenticated,
  //   isLoading,
  //   matchSsoCallback: !!matchSsoCallback,
  //   matchSsoPolling: !!matchSsoPolling,
  //   matchDiagram: !!matchDiagram,
  //   willAllow: !!(matchSsoCallback || matchSsoPolling) || !isAuthenticated
  // });
  
  if (isLoading) {
    // console.log('[NonAuthRouteGuard] Still loading, showing loading screen');
    return <LoadingScreen />;
  }
  
  // Allow access to SSO callback/polling pages even if authenticated
  // This is needed for cross-context SSO (Desktop Outlook) where tokens must be stored
  if (matchSsoCallback || matchSsoPolling) {
    // console.log('[NonAuthRouteGuard] ✓ Allowing access to SSO callback/polling page even though authenticated');
    return children;
  }
  
  if (isAuthenticated && !matchDiagram) {
    // console.log('[NonAuthRouteGuard] User authenticated, redirecting to /folders');
    return <Navigate to="/folders" replace />;
  }

  // console.log('[NonAuthRouteGuard] Rendering children for:', location.pathname);
  return children;
}
